import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryResult,
  UseMutationResult
} from '@tanstack/react-query'
import { useMemo, useRef } from 'react'
import _ from 'lodash'
import { queryKeys } from '../../lib/queryKeys'
import { limitBadgeRequest } from '../../utils/requestLimiter'
import {
  getOrganizations,
  getOrganizationsByIds,
  getOrganizationDetails,
  invalidateOrganizationDetailsCache,
  getOrganizationSettings,
  getOrganizationSensors,
  getOrganizationAiIntegrations,
  getOrganizationAiIntegrationUsage,
  getOrganizationAiIntegrationExplorer,
  triggerAiBackfill,
  revealAiIntegrationToken,
  getGitlabTokenName,
  getBackfillResult,
  getBackfillHistory,
  getOrganizationAiChatHistory,
  getOrganizationAiChatCount,
  getOrganizationAiChatSession,
  getOrganizationAgenticChatHistory,
  getOrganizationAgenticChatSession,
  getOrganizationCustomDashboards,
  getOrganizationCustomDashboard,
  getOrganizationAccounts,
  executeCustomDashboardWidget,
  executeOrganizationSql,
  getOrganizationInsights,
  getOrganizationHasInsights,
  getOrganizationBuyers,
  getOrganizationHealthScore,
  getOrganizationInvoices,
  getOrganizationUsage,
  updateOrganizationSettings,
  updateOrganizationFeatureFlags,
  setOrganizationSupportAccount,
  OrgInsightsResponse,
  OrgHasInsightsResponse,
  OrgBuyersResponse,
  OrgUsageResponse
} from '../../apis/organizations'
import { HAS_US_DATA_SOURCES } from '../../constants/apis'
import type {
  PaginatedResponse,
  Organization,
  OrganizationDetails,
  OrganizationSetting,
  OrganizationSensor,
  OrgAiIntegration,
  OrgAiIntegrationUsage,
  OrgAiIntegrationExplorer,
  AiBackfillTriggerResponse,
  AiIntegrationToken,
  GitlabTokenName,
  AiBackfillResult,
  AiChatHistory,
  AiChatCount,
  AiChatSessionDetail,
  AgenticChatHistory,
  AgenticChatSessionDetail,
  CustomDashboard,
  CustomDashboardDetail,
  OrganizationAccount,
  WidgetExecutionResult,
  OrgHealthScore,
  OrgInvoices
} from '../../types'

interface UseOrganizationsListParams {
  page?: number
  limit?: number
  searchParam?: string | null
  isPaid?: boolean
  isTrial?: boolean
  enabled?: boolean
}

// List query with pagination and filters
export const useOrganizationsList = ({
  page,
  limit,
  searchParam,
  isPaid,
  isTrial,
  enabled = true
}: UseOrganizationsListParams): UseQueryResult<
  PaginatedResponse<Organization>
> => {
  const filters = { page, limit, searchParam, isPaid, isTrial }

  return useQuery({
    queryKey: queryKeys.organizations.list(filters),
    queryFn: async () => {
      const { data } = await getOrganizations(filters)
      // Return full response: { items: [...], total_count: 123 }
      return data
    },
    enabled,
    placeholderData: previousData => previousData
  })
}

// Orgs an org picker shows before typing: favorites first, then the largest paid orgs.
export const useDefaultPickerOrgs = (
  favoriteIds: (string | number)[],
  enabled: boolean
): UseQueryResult<Organization[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.list({ pickerDefaults: favoriteIds }),
    queryFn: async () => {
      // allSettled so a failed favorites lookup still shows the largest orgs.
      const results = await Promise.allSettled([
        favoriteIds.length
          ? getOrganizationsByIds(favoriteIds, { limit: 15 })
          : null,
        getOrganizations({
          limit: 15,
          sortKey: 'billable_users',
          sortDirection: 'desc'
        })
      ])
      const responses = results.flatMap(r =>
        r.status === 'fulfilled' && r.value ? [r.value] : []
      )
      // Rethrow a total failure so React Query retries instead of caching [].
      const failed = results.find(r => r.status === 'rejected')
      if (!responses.length && failed?.status === 'rejected') {
        throw failed.reason
      }
      return _.uniqBy(
        responses.flatMap(r => r.data?.items ?? []),
        'id'
      )
    },
    enabled,
    staleTime: 5 * 60 * 1000
  })
}

// Single organization detail
export const useOrganizationDetail = (
  id: string | number
): UseQueryResult<OrganizationDetails> => {
  return useQuery({
    queryKey: queryKeys.organizations.detail(id),
    queryFn: async () => {
      const { data } = await getOrganizationDetails(id)
      return data
    },
    enabled: !!id
  })
}

// Busts linrest's 24h org-stats cache, then refetches the details
export const useRefreshOrganizationDetail = (
  id: string | number
): UseMutationResult<void, Error, void> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      await invalidateOrganizationDetailsCache(id)
    },
    onSuccess: async () => {
      // exact: detail(id) prefixes every org-scoped query (usage runs a BigQuery scan)
      await Promise.all(
        [
          queryKeys.organizations.detail(id),
          queryKeys.organizations.sensors(id)
        ].map(queryKey =>
          queryClient.invalidateQueries(
            { queryKey, exact: true },
            { throwOnError: true }
          )
        )
      )
    }
  })
}

// Organization settings
export const useOrganizationSettings = (
  id: string | number,
  enabled: boolean = true
): UseQueryResult<OrganizationSetting[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.settings(id),
    queryFn: async () => {
      const { data } = await getOrganizationSettings(id)
      return Array.isArray(data) ? data : []
    },
    enabled: !!id && enabled
  })
}

// Organization sensors
export const useOrganizationSensors = (
  id: string | number
): UseQueryResult<OrganizationSensor[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.sensors(id),
    queryFn: async () => {
      const { data } = await getOrganizationSensors(id)
      return data
    },
    enabled: !!id
  })
}

// Organization AI integrations (method, last activity, distinct users)
export const useOrganizationAiIntegrations = (
  id: string | number
): UseQueryResult<OrgAiIntegration[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiIntegrations(id),
    queryFn: async () => {
      const { data } = await getOrganizationAiIntegrations(id)
      return data
    },
    enabled: !!id
  })
}

// Usage detail (cost, models, tokens) for a single AI integration over a date range
export const useOrganizationAiIntegrationUsage = (
  id: string | number,
  sensorAuthorizationId: string | number | null,
  start: string,
  end: string,
  user = '',
  team = '',
  granularity = 'month'
): UseQueryResult<OrgAiIntegrationUsage> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiIntegrationUsage(
      id,
      sensorAuthorizationId ?? '',
      start,
      end,
      user,
      team,
      granularity
    ),
    queryFn: async () => {
      if (!sensorAuthorizationId) {
        throw new Error('sensorAuthorizationId is required')
      }
      const { data } = await getOrganizationAiIntegrationUsage(
        id,
        sensorAuthorizationId,
        start,
        end,
        user,
        team,
        granularity
      )
      return data
    },
    enabled: !!id && !!sensorAuthorizationId && !!start && !!end
  })
}

// Explorer roll-up (AI spend + dev-productivity metrics) per team and developer
export const useOrganizationAiIntegrationExplorer = (
  id: string | number,
  sensorAuthorizationId: string | number | null,
  start: string,
  end: string
): UseQueryResult<OrgAiIntegrationExplorer> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiIntegrationExplorer(
      id,
      sensorAuthorizationId ?? '',
      start,
      end
    ),
    queryFn: async () => {
      if (!sensorAuthorizationId) {
        throw new Error('sensorAuthorizationId is required')
      }
      const { data } = await getOrganizationAiIntegrationExplorer(
        id,
        sensorAuthorizationId,
        start,
        end
      )
      return data
    },
    enabled: !!id && !!sensorAuthorizationId && !!start && !!end
  })
}

// Fires a backfill; returns job id + before-snapshot + runtime estimate.
export const useTriggerAiBackfill = (
  organizationId: string | number,
  sensorAuthorizationId: string | number | null
): UseMutationResult<
  AiBackfillTriggerResponse,
  Error,
  { since: string; until: string }
> => {
  return useMutation({
    mutationFn: async ({ since, until }) => {
      if (!sensorAuthorizationId) {
        throw new Error('sensorAuthorizationId is required')
      }
      const { data } = await triggerAiBackfill(
        organizationId,
        sensorAuthorizationId,
        since,
        until
      )
      return data
    }
  })
}

// One-shot reveal of the decrypted provider credential. Deliberately a mutation, not a
// query: mutations aren't cached by key, so reset() genuinely drops the token.
export const useRevealAiIntegrationToken = (
  organizationId: string | number
): UseMutationResult<
  AiIntegrationToken,
  Error,
  { sensorAuthorizationId: string | number }
> => {
  return useMutation({
    mutationFn: async ({ sensorAuthorizationId }) => {
      const { data } = await revealAiIntegrationToken(
        organizationId,
        sensorAuthorizationId
      )
      return data
    }
  })
}

// On demand only: each call decrypts the token and hits gitlab.com
export const useGitlabTokenName = (
  organizationId: string | number,
  sensorAuthorizationId: string | number
): UseMutationResult<GitlabTokenName, Error, void> => {
  return useMutation({
    mutationFn: async () => {
      const { data } = await getGitlabTokenName(
        organizationId,
        sensorAuthorizationId
      )
      return data
    }
  })
}

// Loads a persisted before/after record from S3 for the result drawer.
export const useBackfillResult = (
  organizationId: string | number,
  sensorAuthorizationId: string | number | null,
  jobsSuiteUuid: string | null,
  enabled: boolean
): UseQueryResult<AiBackfillResult> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiBackfillResult(
      organizationId,
      sensorAuthorizationId ?? '',
      jobsSuiteUuid ?? ''
    ),
    queryFn: async () => {
      const { data } = await getBackfillResult(
        organizationId,
        sensorAuthorizationId as string | number,
        jobsSuiteUuid as string
      )
      return data
    },
    enabled:
      enabled && !!organizationId && !!sensorAuthorizationId && !!jobsSuiteUuid
  })
}

// Lists an org's past backfills (S3) for the history sheet.
export const useBackfillHistory = (
  organizationId: string | number,
  enabled: boolean
): UseQueryResult<AiBackfillResult[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiBackfillHistory(organizationId),
    queryFn: async () => {
      const { data } = await getBackfillHistory(organizationId)
      return data.results
    },
    enabled: enabled && !!organizationId
  })
}

export const useOrganizationAiChatHistory = (
  id: string | number,
  enabled: boolean = true
): UseQueryResult<AiChatHistory> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiChatHistory(id),
    queryFn: async ({ signal }) => {
      const { data } = await getOrganizationAiChatHistory(id, signal)
      return data
    },
    enabled: !!id && enabled
  })
}

export const useOrganizationAiChatCount = (
  id: string | number
): UseQueryResult<AiChatCount> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiChatCount(id),
    queryFn: async ({ signal }) => {
      const { data } = await limitBadgeRequest(
        () => getOrganizationAiChatCount(id, signal),
        signal
      )
      return data
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000
  })
}

export const useOrganizationAiChatSession = (
  id: string | number,
  sessionKey: string | null
): UseQueryResult<AiChatSessionDetail> => {
  return useQuery({
    queryKey: queryKeys.organizations.aiChatSession(id, sessionKey ?? ''),
    queryFn: async ({ signal }) => {
      const { data } = await getOrganizationAiChatSession(
        id,
        sessionKey!,
        signal
      )
      return data
    },
    enabled: !!id && !!sessionKey
  })
}

export const useOrganizationAgenticChatHistory = (
  id: string | number,
  enabled: boolean = true
): UseQueryResult<AgenticChatHistory> => {
  return useQuery({
    queryKey: queryKeys.organizations.agenticChatHistory(id),
    queryFn: async ({ signal }) => {
      const { data } = await getOrganizationAgenticChatHistory(id, signal)
      return data
    },
    enabled: !!id && enabled
  })
}

export const useOrganizationAgenticChatSession = (
  id: string | number,
  sessionId: string | null
): UseQueryResult<AgenticChatSessionDetail> => {
  return useQuery({
    queryKey: queryKeys.organizations.agenticChatSession(id, sessionId ?? ''),
    queryFn: async ({ signal }) => {
      const { data } = await getOrganizationAgenticChatSession(
        id,
        sessionId!,
        signal
      )
      return data
    },
    enabled: !!id && !!sessionId
  })
}

export const useOrganizationCustomDashboards = (
  id: string | number,
  enabled: boolean = true
): UseQueryResult<CustomDashboard[]> => {
  return useQuery({
    queryKey: queryKeys.organizations.customDashboards(id),
    queryFn: async ({ signal }) => {
      const { data } = await limitBadgeRequest(
        () => getOrganizationCustomDashboards(id, signal),
        signal
      )
      return data
    },
    enabled: !!id && enabled,
    staleTime: 5 * 60 * 1000
  })
}

export const useOrganizationAccounts = (
  id: string | number,
  search: string,
  adminsOnly: boolean,
  enabled: boolean = true
): UseQueryResult<PaginatedResponse<OrganizationAccount>> => {
  return useQuery({
    queryKey: queryKeys.organizations.accounts(id, search, adminsOnly),
    queryFn: async ({ signal }) => {
      const { data } = await getOrganizationAccounts(
        id,
        search,
        adminsOnly,
        signal
      )
      return data
    },
    enabled: !!id && enabled,
    staleTime: 60 * 1000,
    placeholderData: previousData => previousData
  })
}

export const useOrganizationCustomDashboard = (
  id: string | number,
  dashboardId: string | number | null,
  enabled: boolean = true
): UseQueryResult<CustomDashboardDetail> => {
  return useQuery({
    queryKey: queryKeys.organizations.customDashboard(id, dashboardId ?? ''),
    queryFn: async ({ signal }) => {
      const { data } = await getOrganizationCustomDashboard(
        id,
        dashboardId!,
        signal
      )
      return data
    },
    enabled: !!id && dashboardId != null && enabled
  })
}

export const useExecuteCustomDashboardWidget = (
  id: string | number,
  dashboardId: string | number,
  widgetId: string,
  parameters: Record<string, string>,
  enabled: boolean = true,
  targetOrgId?: string
): UseQueryResult<WidgetExecutionResult> => {
  return useQuery({
    queryKey: queryKeys.organizations.customDashboardWidgetExec(
      id,
      dashboardId,
      widgetId,
      parameters,
      targetOrgId
    ),
    queryFn: async ({ signal }) => {
      const { data } = await executeCustomDashboardWidget(
        id,
        dashboardId,
        widgetId,
        parameters,
        targetOrgId,
        signal
      )
      return data
    },
    enabled: !!id && dashboardId != null && !!widgetId && enabled,
    staleTime: 5 * 60 * 1000
  })
}

export const useExecuteOrganizationSql = (): UseMutationResult<
  WidgetExecutionResult,
  Error,
  { organizationId: string; sql: string; parameters: Record<string, string> }
> =>
  useMutation({
    mutationFn: async ({ organizationId, sql, parameters }) => {
      const { data } = await executeOrganizationSql(
        organizationId,
        sql,
        parameters
      )
      return data
    }
  })

// Organization insights
export const useOrganizationInsights = (
  id: string | number | undefined
): UseQueryResult<OrgInsightsResponse> =>
  useQuery({
    queryKey: queryKeys.organizations.insights(id ?? ''),
    queryFn: async () => {
      if (id == null) {
        throw new Error('organizationId is required')
      }
      const { data } = await getOrganizationInsights(id)
      return data
    },
    enabled: id != null && id !== '',
    staleTime: 5 * 60 * 1000
  })

// Force a backend recompute (bypasses the ~1-day server cache) and seed the
// regular insights query with the fresh payload.
export const useRefreshOrganizationInsights = (
  id: string | number | undefined
): UseMutationResult<OrgInsightsResponse, Error, void> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      if (id == null) {
        throw new Error('organizationId is required')
      }
      const { data } = await getOrganizationInsights(id, true)
      return data
    },
    onSuccess: data => {
      queryClient.setQueryData(queryKeys.organizations.insights(id ?? ''), data)
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.hasInsights(id ?? '')
      })
    }
  })
}

const HAS_INSIGHTS_MAX_POLLS = 6

// Separate from useOrganizationInsights so the Organizations table can fan
// out one cheap fetch per visible row without pulling the full items payload
// for every org. Backend hits the same cache, so the two hooks share work.
export const useOrganizationHasInsights = (
  id: string | number | undefined
): UseQueryResult<OrgHasInsightsResponse> => {
  // Consecutive pending responses per org; resets once a result arrives
  const pendingPollsRef = useRef({ id, count: 0 })
  const pendingPolls = () =>
    pendingPollsRef.current.id === id ? pendingPollsRef.current.count : 0
  return useQuery({
    queryKey: queryKeys.organizations.hasInsights(id ?? ''),
    queryFn: async ({ signal }) => {
      if (id == null) {
        throw new Error('organizationId is required')
      }
      // Counted before the request so failed polls also use up the budget
      pendingPollsRef.current = { id, count: pendingPolls() + 1 }
      const { data } = await limitBadgeRequest(
        () => getOrganizationHasInsights(id, signal),
        signal
      )
      if (!data.pending) {
        pendingPollsRef.current = { id, count: 0 }
      }
      return data
    },
    enabled: id != null && id !== '',
    staleTime: 5 * 60 * 1000,
    // The backend keeps generating after a timeout, so poll a few times for the result
    refetchInterval: query =>
      query.state.data?.pending && pendingPolls() < HAS_INSIGHTS_MAX_POLLS
        ? 15 * 1000
        : false
  })
}

export const useOrganizationBuyers = (
  id: string | number | undefined,
  enabled: boolean = true
): UseQueryResult<OrgBuyersResponse> =>
  useQuery({
    queryKey: queryKeys.organizations.buyers(id ?? ''),
    queryFn: async () => {
      if (id == null) {
        throw new Error('organizationId is required')
      }
      const { data } = await getOrganizationBuyers(id)
      return data
    },
    enabled: enabled && id != null && id !== '' && HAS_US_DATA_SOURCES,
    staleTime: 5 * 60 * 1000
  })

// Sourced from BigQuery, so prod-only like the other Salesforce-derived queries
export const useOrganizationHealthScore = (
  id: string | number | undefined
): UseQueryResult<OrgHealthScore> =>
  useQuery({
    queryKey: queryKeys.organizations.healthScore(id ?? ''),
    queryFn: async () => {
      const { data } = await getOrganizationHealthScore(id!)
      return data
    },
    enabled: id != null && id !== '' && HAS_US_DATA_SOURCES,
    staleTime: 5 * 60 * 1000
  })

// Fetched only while the section is open: each uncached org costs a multi-GB BigQuery scan
export const useOrganizationUsage = (
  id: string | number | undefined,
  enabled: boolean
): UseQueryResult<OrgUsageResponse> =>
  useQuery({
    queryKey: queryKeys.organizations.usage(id ?? ''),
    queryFn: async () => {
      const { data } = await getOrganizationUsage(id!)
      return data
    },
    enabled: enabled && id != null && id !== '' && HAS_US_DATA_SOURCES,
    // A failed query is a full BigQuery scan, so don't repeat it
    retry: false,
    staleTime: 5 * 60 * 1000
  })

// Maxio invoices — BigQuery-sourced so prod-only, and admin-only because the endpoint
// requires the admin scope (`enabled` keeps non-admins from firing a guaranteed 403).
export const useOrganizationInvoices = (
  id: string | number | undefined,
  enabled = true
): UseQueryResult<OrgInvoices> =>
  useQuery({
    queryKey: queryKeys.organizations.invoices(id ?? ''),
    queryFn: async () => {
      const { data } = await getOrganizationInvoices(id!)
      return data
    },
    enabled: enabled && id != null && id !== '' && HAS_US_DATA_SOURCES,
    staleTime: 5 * 60 * 1000
  })

interface OrganizationDetailsCompleteResult {
  details?: OrganizationDetails
  settings?: OrganizationSetting[]
  sensors?: OrganizationSensor[]
  sensorsMap: Record<string, OrganizationSensor[]>
  isDetailsLoading: boolean
  isSettingsLoading: boolean
  isSensorsLoading: boolean
  isError: boolean
  error: Error | null
}

// Combined hook for organization details page (parallel fetching)
export const useOrganizationDetailsComplete = (
  id: string | number,
  enableSettings: boolean = true
): OrganizationDetailsCompleteResult => {
  const details = useOrganizationDetail(id)
  const settings = useOrganizationSettings(id, enableSettings)
  const sensors = useOrganizationSensors(id)

  const sensorsMap = useMemo(
    () => (sensors.data ? _.groupBy(sensors.data, 'sensor_type_name') : {}),
    [sensors.data]
  )

  return {
    details: details.data,
    settings: settings.data,
    sensors: sensors.data,
    sensorsMap,
    isDetailsLoading: details.isLoading,
    isSettingsLoading: settings.isLoading,
    isSensorsLoading: sensors.isLoading,
    isError: details.isError || settings.isError || sensors.isError,
    error: details.error || settings.error || sensors.error
  }
}

interface UpdateOrganizationVariables {
  organizationId: string | number
  payload: any
}

// Mutation: Update settings
export const useUpdateOrganizationSettings = (): UseMutationResult<
  any,
  Error,
  UpdateOrganizationVariables
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ organizationId, payload }: UpdateOrganizationVariables) =>
      updateOrganizationSettings(organizationId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.settings(variables.organizationId)
      })
    }
  })
}

// Mutation: Update feature flags
export const useUpdateOrganizationFeatureFlags = (): UseMutationResult<
  any,
  Error,
  UpdateOrganizationVariables
> => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ organizationId, payload }: UpdateOrganizationVariables) =>
      updateOrganizationFeatureFlags(organizationId, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.organizations.settings(variables.organizationId)
      })
    }
  })
}

// Mutation: Set support account
export const useSetSupportAccount = (): UseMutationResult<
  any,
  Error,
  string | number
> => {
  return useMutation({
    mutationFn: (organizationId: string | number) =>
      setOrganizationSupportAccount(organizationId)
  })
}
